import { Component, OnInit } from '@angular/core';
import { EnvService } from 'src/services/env.service';
import { DialogComponent, DialogService, OverlayComponent, ToastComponent, WaitComponent } from '@sircon/sircon-ui-framework';
import { CookieService } from 'ngx-cookie-service';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { MigrationInitializationService } from 'src/app/core/services/migration-initialization.service';
import { BrowserUnsupportedComponent } from 'src/framework/browser-unsupported/browser-unsupported.component';
import { SessionManagementComponent } from 'src/components/session-management/session-management.component';
import { UnifiedHeaderComponent } from 'src/components/header/unified-header.component';
import { PublicHeaderBlackLogoComponent } from 'src/components/header/public-header-black-logo.component';
import { SignupHeaderComponent } from 'src/components/signup/header/signup-header.component';
import { SideRailComponent } from 'src/components/side-rail/side-rail.component';
import { FooterComponent } from 'src/components/footer/footer.component';
import { UserContextService } from 'src/services/user-context.service';
import { RouterEventsService } from 'src/services/router-events.service';
import { combineLatest, delay, distinctUntilChanged, filter, map, Observable, of, startWith, switchMap, takeUntil } from 'rxjs';
import { AsyncPipe, NgIf } from '@angular/common';
import { ReAuthComponent, REAUTH_DIALOG_ID } from 'src/components/re-auth/re-auth.component';
import { ProtectedActionsService } from 'src/services/protected-actions.service';
import { BaseComponent } from '@sircon/sircon-ui-framework';

declare let app;

app.util = app.util || {};
app.util.browserCheck = app.util.browserCheck || {};

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['app.component.less'],
    standalone: true,
    imports: [
        RouterOutlet,
        ToastComponent,
        OverlayComponent,
        DialogComponent,
        BrowserUnsupportedComponent,
        SessionManagementComponent,
        UnifiedHeaderComponent,
        PublicHeaderBlackLogoComponent,
        SignupHeaderComponent,
        SideRailComponent,
        FooterComponent,
        WaitComponent,
        AsyncPipe,
        NgIf,
        ReAuthComponent
    ]
})
export class AppComponent extends BaseComponent implements OnInit {
    // Delay before the navigation spinner appears, so fast in-app transitions don't flash it.
    private readonly NAVIGATION_SPINNER_DELAY_MS = 250;

    // Observables to control layout component visibility
    showSideRail$: Observable<boolean>;
    showUnifiedHeader$: Observable<boolean>;
    showPublicHeader$: Observable<boolean>;
    showSignupHeader$: Observable<boolean>;
    // Hides the static footer on multi-outlet routes, which render their own footer@ outlet
    // (usually EmptyComponent). Without this the default footer always shows - e.g. the
    // finraU4Popup layout should have no footer. See FOF-5501.
    showFooter$: Observable<boolean>;
    // Shows a loading spinner while the router resolves a navigation (guards, resolvers, user
    // context loading). Without this, deep-linking or refreshing a protected route renders a blank
    // page until the EnhancedAuthGuard finishes loading user context. See FOF-5501.
    showNavigationSpinner$: Observable<boolean>;

    constructor(
        private cookieService: CookieService,
        private dialogService: DialogService,
        private env: EnvService,
        private migrationInit: MigrationInitializationService,
        private userContextService: UserContextService,
        private router: Router,
        private routerEventsService: RouterEventsService,
        private protectedActionsService: ProtectedActionsService
    ) {
        super();
        this.migrationInit.initialize();
        // Initialize router event listeners for analytics and session management
        this.routerEventsService.initialize();

        // Route change observable. Deliberately has NO startWith: on a fresh deep-link/hard-refresh,
        // this.router.url is not yet the real destination when this constructor runs (it can read as
        // '/' or stale), so seeding an initial value here computed the wrong chrome for an instant -
        // e.g. isPublicRoute('/') is true, which flips showPublicHeader$ on and showUnifiedHeader$/
        // showSideRail$ off before the first real NavigationEnd corrects it (the flash a user reported
        // reproducing via refresh on /configuration/manageDocuments). Omitting startWith means every
        // combineLatest below simply waits for the first genuine NavigationEnd before emitting at all -
        // same "wait for a real navigation" fix already applied to showFooter$ below.
        const routeChange$ = this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            map(() => this.router.url)
        );

        // Navigation spinner: true while a navigation is in flight, false once it settles.
        // A NavigationStart begins a transition; NavigationEnd/Cancel/Error all end one.
        // The spinner shows only after a short delay so quick transitions don't cause a flash,
        // while a slow guard/resolver (e.g. user context loading on refresh) still surfaces it.
        this.showNavigationSpinner$ = this.router.events.pipe(
            filter(event =>
                event instanceof NavigationStart ||
                event instanceof NavigationEnd ||
                event instanceof NavigationCancel ||
                event instanceof NavigationError
            ),
            map(event => event instanceof NavigationStart),
            distinctUntilChanged(),
            switchMap(isNavigating =>
                isNavigating ? of(true).pipe(delay(this.NAVIGATION_SPINNER_DELAY_MS)) : of(false)
            ),
            startWith(false)
        );

        // User context loaded observable
        const userContextLoaded$ = this.userContextService.context$.pipe(
            map(context => !!context && !!context.user)
        );

        // Show Unified Header on authenticated routes when user context is loaded.
        // usesCustomHeader is currently empty (FOF-7882 emptied every route's header@ outlet in favor
        // of this persistent masthead) but is kept as an extension point; usesNamedOutlets still hides
        // it for the one genuinely chromeless route (viewDocument).
        this.showUnifiedHeader$ = combineLatest([
            userContextLoaded$,
            routeChange$
        ]).pipe(
            map(([hasContext, url]) => hasContext && this.isAuthenticatedRoute(url) && !this.usesCustomHeader(url) && !this.usesNamedOutlets(url))
        );

        // Show Signup Header for specific invite signup routes that override parent header
        this.showSignupHeader$ = routeChange$.pipe(
            map((url) => this.usesSignupHeader(url))
        );

        // Show Public Header on unauthenticated routes OR when user context isn't loaded yet
        // BUT hide it for routes that define their own header via named outlets OR use signup header
        this.showPublicHeader$ = combineLatest([
            userContextLoaded$,
            routeChange$
        ]).pipe(
            map(([hasContext, url]) => (!hasContext || this.isPublicRoute(url)) && !this.usesNamedOutlets(url) && !this.usesSignupHeader(url))
        );

        // Show SideRail on authenticated routes when user context is loaded, EXCEPT routes that
        // render their own chrome (rendersOwnChrome = named outlets or a MultiOutletContainer route).
        // Sharing that predicate with the footer keeps the two from diverging (the sideRail leaked
        // onto viewDocument because this used to check only usesCustomSideRail, not named outlets).
        this.showSideRail$ = combineLatest([
            userContextLoaded$,
            routeChange$
        ]).pipe(
            map(([hasContext, url]) => hasContext && this.isAuthenticatedRoute(url) && !this.rendersOwnChrome(url))
        );

        // Show the static footer everywhere EXCEPT routes that render their own chrome (same
        // rendersOwnChrome predicate as the side rail, so they can't drift apart).
        //
        // Start hidden and only show once a navigation actually resolves: routeChange$'s
        // startWith(router.url) is '/' on a fresh deep-link (before the initial navigation settles),
        // which would briefly show the footer on a popup route like finraU4Popup and then hide it -
        // a visible flash. Deferring to the first NavigationEnd avoids that (side rail avoids it by
        // gating on userContextLoaded$, which likewise starts false).
        this.showFooter$ = this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            map(() => !this.rendersOwnChrome(this.router.url)),
            startWith(false)
        );
    }

    /**
     * Check if current route is public/unauthenticated
     */
    private isPublicRoute(url: string): boolean {
        const publicRoutes = [
            '/login', '/forgotPassword', '/resetPassword', '/expiredPassword',
            '/authenticate', '/ssoLogin', '/ssoExpired', '/ssoInvite', '/ssoProducerNotFound',
            '/agencyLogin', '/error', '/signInHelpWorkflow',
            '/signup', '/inviteSignup'
        ];
        return publicRoutes.some(route => {
             return url === route || url.startsWith(route + '/') || url.startsWith(route + '?');
        }) || url === '/' || url === '';
    }

    /**
     * Check if current route requires authentication
     */
    private isAuthenticatedRoute(url: string): boolean {
        return !this.isPublicRoute(url);
    }


    /**
     * Check if current route uses a custom header (replaces default UnifiedHeaderComponent)
     * These routes use MultiOutletContainerComponent and define their own header@ outlet
     */
    private usesCustomHeader(url: string): boolean {
        const customHeaderRoutes = [
            // FOF-7882: All workflow routes that previously defined a header@ outlet now use
            // EmptyComponent there, relying on the app-level UnifiedHeader (global masthead).
            // usesCustomHeader is now empty — every multi-outlet route either uses EmptyComponent
            // in the header outlet (so rendersOwnChrome() stays true via usesCustomSideRail/Footer)
            // or is a single-outlet route that never had a header outlet. The signup/public routes
            // (/docusignLanding, /account-recovery-workflow, /freePremiumTrial) use HeaderContentContainer
            // or a direct component — no named outlets — so they don't need this list either.
        ];
        return customHeaderRoutes.some(route => {
            // Exact match or must be followed by / or ? to avoid false positives
            // e.g., '/workflow' should match '/workflow/step1' but not '/workflows'
            return url === route || url.startsWith(route + '/') || url.startsWith(route + '?');
        });
    }

    /**
     * Check if current route uses a custom side rail (replaces default SideRailComponent)
     * These routes define sideRail@ outlet but may or may not define header@
     */
    private usesCustomSideRail(url: string): boolean {
        const customSideRailRoutes = [
            // All MultiOutletContainer routes that define sideRail@
            '/workflow',
            '/contact-changes',
            '/license-print',
            '/beneficiaryRedirect',
            '/bulk-renewals',
            '/manage-associations',
            '/maintain-background-questions',
            '/admin/manageObaTypes',
            '/shareDocuments',
            '/multipleProducerShareDocuments',
            '/producerShareDocument',
            '/paymentProfile/createPaymentMethod',
            '/maintainResponses',
            '/filingWorkflow',
            '/filingWorkflowMultipleDisclosures',
            '/connectionWorkflow',
            '/carrierConnectWorkflow',
            '/producerContractWorkflow',
            '/manageContractConfigVersionWorkflow',
            '/createContractConfigurationVersion',
            '/createMinorContractConfigurationVersion',
            '/carrierContractWorkflow',
            '/contractConfigurationWorkflow',
            '/producerCompScheduleWorkflow',
            '/compScheduleConfigurationWorkflow',
            '/manageProducerWorkflow',
            '/manageBeneficiariesWorkflow',
            '/subscriptionUpgradeWorkflow',
            '/agencySubscriptionWorkflow',
            '/admin/editWorkflow',
            '/consoleWorkflow',
            '/configureQuestionnaireWorkflow',
            '/bulkRequestQuestionnaireResponses',
            '/modifyHierarchyNodeHistoricalPeriods',
            '/modifyProducerTerminations',
            '/transfer-license-status',
            '/ce-transcript',
            '/reporting/pentaho',
            '/finraU4Popup',
            '/viewContractFile',
            '/producerContractSigning',
            '/docusignLanding',
            '/addInsuranceWorkflow',
            '/docusignTemplateWorkflow',
            '/setupSellingRelationship',
            '/modifySellingRelationship',
            '/createWritingProfile',
            '/modifyHierarchy',
            '/createHierarchy',
            '/updateHierarchy',
            '/account-recovery-workflow',
            '/addPaymentPreference',
            '/framedTransactionTab',
            '/commercial-designations',
            '/freePremiumTrial',
            '/compliance/create',
            '/ProducerNotifications',
            '/manageProducerGroup',
            '/accountSecurity',
            '/maintainCardProfile',
            // profile-config.routes.ts - Routes with custom side rails but DEFAULT header
            '/terminationReasonsWorkflow',
            '/paymentPreferencesWorkflow',
            '/errorsAndOmissionsWorkflow',
            '/configureAccessPermissions',
            '/configureCarrierFeatureChanges',
            '/configureProfile',
            '/manageSellingRelationshipTerminationPeriods',
            // FOF-7882: removed from usesCustomHeader (header outlet now Empty); kept here so
            // rendersOwnChrome() stays true and the static side rail/footer stay hidden.
            '/producer-interview-workflow',
        ];
        return customSideRailRoutes.some(route => {
            // Exact match or must be followed by / or ? to avoid false positives
            // e.g., '/workflow' should match '/workflow/step1' but not '/workflows'
            return url === route || url.startsWith(route + '/') || url.startsWith(route + '?');
        });
    }

    /**
     * Check if the current route provides its own footer@ outlet (all MultiOutletContainerComponent
     * routes). For these the static <app-footer> must be hidden - the route's own footer outlet
     * (usually EmptyComponent) governs the footer, matching the original UI-Router footer@ views.
     */
    private usesCustomFooter(url: string): boolean {
        const customFooterRoutes = [
            '/ProducerNotifications',
            '/account-recovery-workflow',
            '/accountSecurity',
            '/addInsuranceWorkflow',
            '/addPaymentPreference',
            '/admin/editWorkflow',
            '/admin/manageObaTypes',
            '/agencySubscriptionWorkflow',
            '/beneficiaryRedirect',
            '/bulk-renewals',
            '/carrierConnectWorkflow',
            '/carrierContractWorkflow',
            '/ce-transcript',
            '/commercial-designations',
            '/compScheduleConfigurationWorkflow',
            '/compliance/create',
            '/configureAccessPermissions',
            '/configureCarrierFeatureChanges',
            '/configureProfile',
            '/configureQuestionnaireWorkflow',
            '/connectionWorkflow',
            '/consoleWorkflow',
            '/contact-changes',
            '/contractConfigurationWorkflow',
            '/createContractConfigurationVersion',
            '/createHierarchy',
            '/createMinorContractConfigurationVersion',
            '/docusignLanding',
            '/docusignTemplateWorkflow',
            '/filingWorkflow',
            '/filingWorkflowMultipleDisclosures',
            '/finraU4Popup',
            '/framedTransactionTab',
            '/freePremiumTrial',
            '/license-print',
            '/maintain-background-questions',
            '/maintainCardProfile',
            '/maintainResponses',
            '/manage-associations',
            '/manageBeneficiariesWorkflow',
            '/manageContractConfigVersionWorkflow',
            '/manageProducerGroup',
            '/manageProducerWorkflow',
            '/modifyHierarchy',
            '/modifyHierarchyNodeHistoricalPeriods',
            '/modifyProducerTerminations',
            '/modifySellingRelationship',
            '/createWritingProfile',
            '/multipleProducerShareDocuments',
            '/paymentPreferencesWorkflow',
            '/errorsAndOmissionsWorkflow',
            '/paymentProfile/createPaymentMethod',
            '/producerCompScheduleWorkflow',
            '/producerContractSigning',
            '/producerContractWorkflow',
            '/producerShareDocument',
            '/reporting/pentaho',
            '/setupSellingRelationship',
            '/shareDocuments',
            '/subscriptionUpgradeWorkflow',
            '/terminationReasonsWorkflow',
            '/transfer-license-status',
            '/updateHierarchy',
            '/viewCompScheduleFile',
            '/viewContractFile',
            '/workflow/interview',
            // FOF-7882: removed from usesCustomHeader (header outlet now Empty); kept here so
            // rendersOwnChrome() stays true and the static side rail/footer stay hidden.
            '/producer-interview-workflow',
        ];
        return customFooterRoutes.some(route => {
            // Exact match or must be followed by / or ? to avoid false positives
            return url === route || url.startsWith(route + '/') || url.startsWith(route + '?');
        });
    }

    /**
     * Check if current route is genuinely chromeless - no header, footer, or side rail at all.
     * FOF-7882 emptied the header@ outlet on shareDocuments/multipleProducerShareDocuments/
     * producerShareDocument/paymentProfile/maintainResponses/admin/manageObaTypes so the app-level
     * UnifiedHeader (global masthead) would show through instead - they were removed from this list
     * accordingly. viewDocument is the one route that empties header, footer, AND sideRail@ outlets
     * (a print/view-only layout), so it alone stays here to keep the persistent global header hidden.
     */
    private usesNamedOutlets(url: string): boolean {
        const namedOutletRoutes = [
            '/viewDocument'
        ];
        return namedOutletRoutes.some(route => url.startsWith(route));
    }

    /**
     * Single source of truth for "this route supplies its own chrome" - it renders its own
     * header/sideRail/footer via named outlets and/or is a MultiOutletContainerComponent route.
     * The static side rail AND static footer both hide whenever this is true, so they can never
     * drift apart again (three separate bugs this session came from these two lists diverging:
     * footer-always-on, footer-flash, sideRail-leaked-on-viewDocument).
     *
     * NOTE: the header intentionally does NOT use this - ~25 routes keep the DEFAULT unified header
     * while replacing only the side rail, so header visibility keeps its own usesCustomHeader test.
     */
    private rendersOwnChrome(url: string): boolean {
        return this.usesNamedOutlets(url)
            || this.usesCustomHeader(url)
            || this.usesCustomSideRail(url)
            || this.usesCustomFooter(url);
    }

    /**
     * Check if current route uses SignupHeaderComponent
     *
     * IMPORTANT: This is the preferred pattern for public routes that need custom headers.
     * Instead of using named outlets (which don't work reliably for root-level routes),
     * routes are added to this list and AppComponent conditionally renders the header.
     *
     * Pattern discovered during FOF-5501 migration after 16 failed attempts with named outlets.
     * See docs/routing/PUBLIC_ROUTE_PATTERNS.md for detailed explanation.
     *
     * Routes in this list:
     * - Child routes that override parent headers (e.g., inviteSignup children)
     * - Root-level public routes needing SignupHeader (e.g., resetPasswordInactivity)
     */
    private usesSignupHeader(url: string): boolean {
        const signupHeaderRoutes = [
            '/inviteSignup/initiateInvite',
            '/inviteSignup/initiatePending',
            '/agencyDecline',
            '/resetPasswordInactivity',
            '/activateEmailAddressChange',
            '/activatePendingInsuranceEmail',
            '/retainInsuranceAccount'
        ];
        return signupHeaderRoutes.some(route => url.startsWith(route));
    }

    ngOnInit() {
        if (!this.isBrowserSupported(app.util.browserCheck) && !this.hasUserIgnoredBrowserDialog()) {
            this.dialogService.setAutoOpenId('browser-unsupported-dialog');
        }

        this.protectedActionsService.reAuthRequired$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => this.dialogService.open(REAUTH_DIALOG_ID));
    }

    closeReAuthDialog(): void {
        this.dialogService.close(REAUTH_DIALOG_ID);
    }

    isBrowserSupported(browserObject) {
        return browserObject ? browserObject.supported : false;
    }

    hasUserIgnoredBrowserDialog() {
        return this.cookieService.get(this.env.storageKeys.BROWSER_UNSUPPORTED) === 'true';
    }
}
